// Ported from src/hooks/useAudioRecorder.js on 2026-10-05.
import { useRef, useState } from 'react';
import { createRecordingBlob, getMicrophoneErrorMessage } from '../utils/recording.utils';

const DURATION_TICK_MS = 200;
const DEFAULT_MAX_DURATION = 1000 * 60 * 60;

export const useAudioRecorder = (fileType = 'mp3') => {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioBlobRef = useRef<Blob | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [active, setActive] = useState(false);
  const [duration, setDuration] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [maxDuration, setMaxDuration] = useState(DEFAULT_MAX_DURATION);
  const [micError, setMicError] = useState('');

  // Lazy — called only when the user first clicks record
  const initializeRecorder = async () => {
    if (recorderRef.current) return; // already initialized

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      throw new Error(getMicrophoneErrorMessage(err));
    }

    const recorder = new MediaRecorder(stream);
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data) audioChunksRef.current.push(e.data);
    };

    recorder.onstart = () => {
      clearAudio();
      startTimeRef.current = performance.now();
      intervalRef.current = setInterval(updateDuration, DURATION_TICK_MS);
      timeoutRef.current = setTimeout(stop, maxDuration);
    };

    recorder.onstop = () => {
      if (audioChunksRef.current.length > 0) {
        audioBlobRef.current = createRecordingBlob(audioChunksRef.current, fileType);
        setBlob(audioBlobRef.current);
      } else console.error('No audio chunks in recording');

      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    };
  };

  const toggleRecording = (maxDuration?: number) => {
    if (active) stop();
    else {
      setMaxDuration(maxDuration || DEFAULT_MAX_DURATION);
      startRecording();
    }
  };

  const clearAudio = () => {
    audioChunksRef.current = [];
    audioBlobRef.current = null;
    setBlob(null);
    setDuration(0);
  };

  const updateDuration = () => {
    const timeSinceStart = performance.now() - startTimeRef.current;
    setDuration(timeSinceStart);
  };

  const startRecording = async () => {
    setMicError('');
    try {
      await initializeRecorder();
    } catch (err) {
      setMicError(err.message);
      return;
    }

    setActive(true);
    setDuration(0);
    recorderRef.current.start();
  };

  const stop = () => {
    setActive(false);

    recorderRef.current.stop();
  };

  return {
    blob,
    recording: active,
    duration,
    micError,
    toggleRecording,
    clearAudio,
  };
};
